import { motion, AnimatePresence } from 'framer-motion'
import React, { useEffect, useRef, useState } from 'react'
import { useCursor } from '../cursor/useCursor'
import { CURSORS } from '../cursor/cursors'

const TITLE_HOLD = 300
const ACCORDION_SCROLL = 600
const END_HOLD = 400
const LERP = 0.1

const clamp01 = (v) => Math.min(1, Math.max(0, v))
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3)

function OnGoingContainer() {
    const [activeCard, setActiveCard] = useState(0)
    const sectionRef = useRef(null)
    const panelRef = useRef(null)
    const rafRef = useRef(null)
    const smoothScroll = useRef(0)
    const { cursor } = useCursor()

    const tdata = [
        {
            title: "Flutter Performance & Efficiency",
            desc: "A research-driven study investigating how Flutter coding practices, widget composition, state management, architecture, rendering patterns, and resource handling affect application performance. Different implementations will be benchmarked using metrics such as FPS, frame rendering time, CPU usage, memory consumption, startup time, and application size to identify measurable relationships between coding practices and application efficiency.",
            link: "#",
            status: "on going",
            type: "case study"
        },
        {
            title: "Aureon",
            desc: "Aureon is a modern Flutter-based expense tracker that helps users manage daily expenses, monthly budgets, recurring bills, and savings goals. It provides spending insights and budget tracking through a sleek, futuristic fintech interface designed to make personal finance simple and intuitive.",
            link: "#",
            status: "on going",
            type: "project"
        },
        {
            title: "Flutter Performance Model",
            desc: "A machine learning project built using the performance dataset collected from the case study. The model will learn patterns between Flutter code characteristics and measured performance metrics, enabling it to identify potentially inefficient coding practices and predict their impact on application performance. The trained model will later serve as the intelligence layer for the final performance analysis platform.",
            link: "#",
            status: "up coming",
            type: "model training"
        },
        {
            title: "Flutter Performance Analyzer",
            desc: "A developer-focused platform that analyzes Flutter codebases through a GitHub repository or local project and provides a comprehensive performance assessment. It will detect inefficient coding patterns, highlight potential bottlenecks, generate a performance score, and provide practical recommendations for improving the efficiency and quality of Flutter applications.",
            link: "#",
            status: "up coming",
            type: "project"
        },
    ]

    useEffect(() => {
        const section = sectionRef.current
        const panel = panelRef.current
        if (!section) return

        const tick = () => {
            const rect = section.getBoundingClientRect()
            const targetScroll = Math.max(0, -rect.top)

            smoothScroll.current += (targetScroll - smoothScroll.current) * LERP
            const scrolled = smoothScroll.current

            const progress = easeOutCubic(
                clamp01((scrolled - TITLE_HOLD) / ACCORDION_SCROLL)
            )
            const translateVh = (1 - progress) * 100

            if (panel) {
                panel.style.transform = `translateY(${translateVh}vh)`
            }

            rafRef.current = requestAnimationFrame(tick)
        }

        rafRef.current = requestAnimationFrame(tick)
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current)
        }
    }, [])

    const trackHeight = `calc(100vh + ${TITLE_HOLD + ACCORDION_SCROLL + END_HOLD}px)`

    return (
        <section
            {...cursor({ ...CURSORS.project, background: "#fff", blendMode: "difference", size: 20, text: "" })}
            ref={sectionRef}
            className="relative bg-black"
            style={{ height: trackHeight }}
        >
            <div className="sticky top-0 h-screen overflow-hidden">

                {/* ================= TITLE ================= */}
                <div
                    className="
                        absolute inset-0 z-[1]
                        flex flex-col items-center justify-center
                        pointer-events-none
                        px-4
                    "
                    style={{
                        backgroundImage: `
                            linear-gradient(rgba(255,255,255,0.078) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(255,255,255,0.099) 1px, transparent 1px)
                        `,
                        backgroundSize: "clamp(90px, 22vw, 210px) clamp(80px, 20vw, 190px)",
                    }}
                >
                    <div className="mb-3 font-minecraft text-[10px] sm:text-xs md:text-[0.78rem] tracking-[3px] sm:tracking-[4px] text-[#666]">
                        WHAT'S
                    </div>
                    <div className="font-minecraft text-[clamp(2rem,9vw,5rem)] leading-[1.1] tracking-[2px] sm:tracking-[15px] text-center text-white">
                        ON GOING
                    </div>
                    <div className="mb-3 font-minecraft text-[10px] sm:text-xs md:text-[0.78rem] tracking-[3px] sm:tracking-[4px] text-[#666]">
                        & up coming
                    </div>
                </div>

                {/* ================= ACCORDION PANEL ================= */}
                <div
                    {...cursor({ ...CURSORS.project, text: "view", background: '', border: '1px solid #fff', blendMode: "difference", size: 150 })}
                    ref={panelRef}
                    className="absolute inset-0 z-[2] will-change-transform"
                    style={{ transform: 'translateY(100vh)' }}
                >
                    <div

                        className="
                            h-full bg-black
                            p-4 sm:p-8 md:p-14 lg:p-24
                            flex flex-col lg:flex-row
                        "
                        style={{
                            backgroundImage: `
                                linear-gradient(rgba(255,255,255,0.078) 1px, transparent 1px),
                                linear-gradient(90deg, rgba(255,255,255,0.099) 1px, transparent 1px)
                            `,
                            backgroundSize: "clamp(90px, 22vw, 210px) clamp(80px, 20vw, 190px)",
                        }}
                    >
                        {tdata.map((item, index) => {
                            const isActive = activeCard === index
                            return (
                                <motion.div
                                    layout
                                    key={index}
                                    onMouseEnter={() => setActiveCard(index)}
                                    onClick={() => setActiveCard(index)}
                                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                                    className={`
                                        relative cursor-pointer
                                        my-1.5 lg:mx-1.5
                                        rounded-[48px] sm:rounded-[58px] lg:rounded-[64px]
                                        overflow-hidden
                                        border
                                        backdrop-blur-xl
                                        transition-colors duration-700
                                        ${isActive
                                            ? 'flex-[10] bg-white/[0.08] border-white/20'
                                            : 'flex-1 bg-white/[0.03] border-white/10 hover:bg-white/[0.06]'
                                        }
                                    `}
                                    style={{
                                        minHeight: '96px',
                                        boxShadow: isActive
                                            ? '0 0 0 1px rgba(255,255,255,0.04) inset, 0 20px 60px -20px rgba(0,0,0,0.6)'
                                            : 'none',
                                    }}
                                >
                                    {/* number tag */}
                                    <span className="absolute top-4 left-4 sm:top-6 sm:left-6 flex items-center gap-2 font-pixeloperator text-[18px] tracking-[2px] text-white/30">
                                        <span>{String(index + 1).padStart(2, '0')}</span>
                                        {item.type && isActive && (
                                            <span className="normal-case text-[#C8F135]">· {item.type}</span>
                                        )}
                                    </span>

                                    {item.status && isActive && (
                                        <span
                                            className={`
                                            absolute top-4 right-4 sm:top-6 sm:right-6
                                            font-minecraft text-[9px] tracking-[1px] normal-case
                                            px-2 py-1 rounded-full border
                                            ${item.status === 'on going'
                                                    ? 'border-[#C8F135]/40 text-[#C8F135]'
                                                    : 'border-orange-300/40 text-orange-300'
                                                }
                                            `}
                                        >
                                            {item.status}
                                        </span>
                                    )}

                                    {/* collapsed: vertical title */}
                                    {!isActive && (
                                        <div className="hidden lg:flex h-full w-full items-center justify-center">
                                            <h3
                                                className="
                                                    font-minecraft text-xs tracking-[3px]
                                                    text-white/70
                                                    [writing-mode:vertical-rl]
                                                    rotate-180
                                                    whitespace-nowrap
                                                "
                                            >
                                                {item.title}
                                            </h3>
                                        </div>
                                    )}

                                    {/* mobile / small screens: always horizontal collapsed row */}
                                    {!isActive && (
                                        <div className="flex lg:hidden h-full w-full items-center px-6 py-5">
                                            <h3 className="font-minecraft text-sm tracking-[2px] text-white/70">
                                                {item.title}
                                            </h3>
                                        </div>
                                    )}

                                    {/* expanded content */}
                                    <AnimatePresence>
                                        {isActive && (
                                            <motion.div
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                exit={{ opacity: 0 }}
                                                transition={{ duration: 0.4, delay: 0.2 }}
                                                className="
        h-full w-full min-w-0
        flex flex-col justify-between
        px-5 py-10
        sm:px-7 sm:py-12
        md:px-10 md:py-16
    "
                                            >
                                                <div>
                                                    <h2 className="
                font-pixeloperator
                text-[clamp(1.4rem,5vw,3.75rem)]
                leading-[1.05]
                tracking-[0.5px] sm:tracking-[1px]
                text-white
                mb-4
                max-w-full
                break-words
                overflow-wrap-anywhere
            ">
                                                        {item.title}
                                                    </h2>
                                                    <p
                                                        className="
                text-[11px]
                sm:text-xs
                md:text-[14px]
                font-['Space_Mono']
                text-white/60
                max-w-md
                w-full
                break-words
                leading-relaxed
            "
                                                    >
                                                        {item.desc}
                                                    </p>
                                                </div>

                                                {item.link !== "#" && (
                                                    <a
                                                        href={item.link}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="
                                                            group inline-flex items-center gap-2 w-fit
                                                            mt-6
                                                            text-xs sm:text-sm tracking-[1px]
                                                            text-white/80 hover:text-white
                                                            border-b border-white/20 hover:border-white/60
                                                            pb-1
                                                            transition-colors duration-300
                                                        "
                                                    >
                                                        View case study
                                                        <span className="transition-transform duration-300 group-hover:translate-x-1">
                                                            →
                                                        </span>
                                                    </a>
                                                )}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            )
                        })}
                    </div>
                </div>
            </div>
        </section>
    )
}

export default OnGoingContainer
